/**
 * 收银台监听工具 - 辅助函数
 * @author Nemo
 * @date 2026-01-26
 */

/**
 * 安全的错误上报（避免循环调用）
 * @param {Object} vm - Vue实例
 * @param {Error} error - 错误对象
 * @param {String} context - 错误上下文
 */
export function reportError(vm, error, context) {
  try {
    if (vm && typeof vm.sendRiskDeviceFingerPrint === 'function') {
      vm.sendRiskDeviceFingerPrint({
        e: 'code_error',
        lmo: JSON.stringify({
          context: `checkout_monitor_${context}`,
          message: error?.message || String(error),
          stack: error?.stack?.substring(0, 200), // 只取前200字符
        }),
      });
    }
  } catch (e) {
    // 静默失败，避免无限递归
  }
}

/**
 * 查找元素（支持ref和selector）
 * @param {Object} options - 查找选项
 * @param {Object} options.vm - Vue实例
 * @param {String} options.ref - ref名称
 * @param {String} options.selector - CSS选择器
 * @returns {Element|null} DOM元素
 */
export function findElement({ vm, ref, selector }) {
  try {
    // 优先使用ref
    if (ref && vm && vm.$refs && vm.$refs[ref]) {
      const refValue = vm.$refs[ref];

      // 处理ref数组情况（多个同名ref）
      if (Array.isArray(refValue)) {
        const element = refValue.find(el => el);
        return element?.$el || element || null;
      }

      // 处理单个ref（可能是Vue组件或DOM元素）
      return refValue.$el || refValue;
    }

    // 使用CSS选择器
    if (selector && typeof document !== 'undefined') {
      return document.querySelector(selector);
    }

    return null;
  } catch (error) {
    // 捕获异常，返回 null
    return null;
  }
}

/**
 * 检查元素是否已渲染（有尺寸）
 * @param {Element} element - DOM元素
 * @returns {Boolean} 是否已渲染
 */
export function isElementRendered(element) {
  try {
    if (!element) return false;
    // 确保是 DOM 元素
    if (typeof element.offsetWidth !== 'number') return false;
    return element.offsetWidth > 0 && element.offsetHeight > 0;
  } catch (error) {
    return false;
  }
}

/**
 * 重试查找元素
 * @param {Function} finder - 查找函数
 * @param {Object} options - 重试配置
 * @param {Number} options.maxRetry - 最大重试次数
 * @param {Number} options.delay - 重试延迟(ms)
 * @param {Function} options.onComplete - 完成回调(成功或失败都会调用)
 * @param {Object} options.vm - Vue实例（用于错误上报）
 * @returns {Function} 取消函数
 */
export function retryFind(finder, options = {}) {
  const {
    maxRetry = 3,
    delay = 500,
    onComplete,
    vm,
  } = options;

  let timerId = null;
  let cancelled = false;

  const attempt = (retryCount) => {
    // 如果已取消，停止执行
    if (cancelled) return;

    try {
      const element = finder();

      // 成功：找到元素且已渲染
      if (element && isElementRendered(element)) {
        onComplete?.(element, true); // 成功
        return;
      }

      // 还有重试机会
      if (retryCount < maxRetry) {
        timerId = setTimeout(() => attempt(retryCount + 1), delay);
        return;
      }

      // 重试耗尽 = 失败（成功的取反）
      onComplete?.(null, false); // 失败
    } catch (error) {
      // 捕获异常，上报错误
      reportError(vm, error, 'retry_find');
      onComplete?.(null, false);
    }
  };

  attempt(0);

  // 返回取消函数
  return () => {
    cancelled = true;
    if (timerId) {
      clearTimeout(timerId);
      timerId = null;
    }
  };
}
